import React, { useRef, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { formatoMoneda } from "../../configuracion/validaciones";

ChartJS.register(ChartDataLabels, CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const Barras = ({ datos, esMonetario, altura = '300px' }) => {
  const scrollContainerRef = useRef(null);

  const numPuntos = datos?.labels?.length || 0;
  // Si hay más de 12 puntos (ej. más de una semana, o muchos productos), se habilita el desplazamiento horizontal
  const necesitaScroll = numPuntos > 12;
  const anchoCalculado = necesitaScroll ? `${Math.max(numPuntos * 45, 600)}px` : '100%';

  // Al cargar o cambiar datos, desplazamos suavemente hacia la derecha para mostrar los registros más recientes
  useEffect(() => {
    if (scrollContainerRef.current && necesitaScroll) {
      scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
    }
  }, [datos, necesitaScroll]);

  // Validación de seguridad para evitar que el componente se rompa si 'datos' es null o undefined
  if (!datos || !datos.labels || datos.labels.length === 0) {
    return <div className="text-center py-3 text-muted">No hay datos para mostrar</div>;
  }

  // LÓGICA DE DETECCIÓN Y CONSTRUCCIÓN DE DATASET:
  // Si trae datasets (comparativo, ej. Contado vs Crédito), se usa directamente con sus labels en X.
  // Si no trae datasets, se construye un dataset único con los labels en el eje X para que cada barra
  // tenga su propia posición y no se amontonen en una sola posición vacía.
  const chartData = {
    labels: datos.labels,
    datasets: datos.datasets
      ? datos.datasets.map(ds => ({
          ...ds,
          borderRadius: 4,
          maxBarThickness: 35,
        }))
      : [
          {
            label: datos.titulo || 'Total',
            data: datos.data,
            backgroundColor: datos.colors || '#4e73df',
            borderColor: 'rgba(0, 0, 0, 0.05)',
            borderWidth: 1,
            borderRadius: 4,
            maxBarThickness: 35,
          },
        ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: numPuntos > 50 ? 0 : 400,
    },
    plugins: {
      legend: {
        // Mostrar leyenda solo si hay datasets múltiples (ej. comparativos) o si se pide explícitamente
        display:
          datos.mostrarLeyenda !== undefined
            ? Boolean(datos.mostrarLeyenda)
            : Boolean(datos.datasets && datos.datasets.length > 1),
        position: 'top',
        labels: { boxWidth: 14, usePointStyle: true, font: { size: 11 } }
      },
      title: {
        display: datos.mostrarTitulo !== undefined ? Boolean(datos.mostrarTitulo) : Boolean(datos.titulo),
        text: datos.titulo || '',
        font: { size: 13, weight: 'bold' },
        padding: { bottom: 10 },
      },
      datalabels: {
        // Ocultar etiquetas si el valor es 0 o null
        display: (context) => {
          const val = context?.dataset?.data?.[context.dataIndex];
          if (val === undefined || val === null || parseFloat(val) === 0) return false;
          // Si no hay scroll y hay demasiadas barras, ocultar para evitar solapamiento
          if (!necesitaScroll && numPuntos > 15) return false;
          return true;
        },
        anchor: 'end',
        align: 'top',
        offset: 3,
        font: { weight: 'bold', size: 9 },
        formatter: (value) => {
          if (value === undefined || value === null || value === 0) return '';
          if (esMonetario) {
            const num = parseFloat(value);
            if (Math.abs(num) >= 1000000) return `L ${(num / 1000000).toFixed(1)}M`;
            if (Math.abs(num) >= 10000) return `L ${(num / 1000).toFixed(1)}k`;
            return formatoMoneda(num);
          }
          return typeof value === 'number' ? value.toLocaleString() : value;
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        padding: 10,
        cornerRadius: 6,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || context.label || '';
            let value = context.raw || 0;
            return ` ${label}: ${esMonetario ? formatoMoneda(value) : (typeof value === 'number' ? value.toLocaleString() : value)}`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: !necesitaScroll,
          maxRotation: 45,
          minRotation: 0,
          font: { size: 10 },
        }
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(255, 255, 255, 0.08)' },
        ticks: {
          precision: esMonetario ? undefined : 0,
          callback: function (value) {
            if (esMonetario) {
              if (Math.abs(value) >= 1000000) return `L ${(value / 1000000).toFixed(1)}M`;
              if (Math.abs(value) >= 1000) return `L ${(value / 1000).toFixed(0)}k`;
              return formatoMoneda(value);
            }
            return Number.isInteger(value) ? value : '';
          },
          font: { size: 10 },
        }
      }
    },
  };

  return (
    <div className="w-100" style={{ position: 'relative', height: altura, overflow: 'hidden' }}>
      {necesitaScroll && (
        <div className="d-flex justify-content-between align-items-center mb-1 text-muted" style={{ fontSize: '0.75rem' }}>
          <span>
            <i className="fas fa-arrows-alt-h me-1 text-primary"></i>
            Desplaza horizontalmente para explorar el período ({numPuntos} registros)
          </span>
          <span className="badge text-primary border" style={{ backgroundColor: 'var(--n8-shell, rgba(255,255,255,0.06))' }}>
            <i className="fas fa-exchange-alt me-1"></i> Desplazable
          </span>
        </div>
      )}
      <div
        ref={scrollContainerRef}
        style={{
          overflowX: necesitaScroll ? 'auto' : 'hidden',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: necesitaScroll ? '6px' : '0',
          height: necesitaScroll ? 'calc(100% - 24px)' : '100%',
          width: '100%',
        }}
      >
        <div style={{ height: '100%', minWidth: anchoCalculado, width: '100%', position: 'relative' }}>
          <Bar data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

export default Barras;
